import { supabase } from './supabase';

/**
 * Escape PostgreSQL ILIKE wildcards in user-supplied search terms.
 * Without this, a search for "%" matches every row (full-table exposure),
 * and "_" matches any single character (unintended partial matches).
 */
function escapeLike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}
import type {
  JobOffer,
  JobOfferContractType,
  JobOfferRemoteType,
  JobOfferStatus,
} from '@/types/offres';

export interface JobOfferFilters {
  contractType?: JobOfferContractType | 'all';
  remoteType?: JobOfferRemoteType | 'all';
  locationCity?: string;
}

export interface JobOfferInput {
  title: string;
  slug: string;
  contract_type: JobOfferContractType;
  location_city: string;
  remote_type: JobOfferRemoteType;
  description: string;
  profile: string;
  status: JobOfferStatus;
  internal_reference?: string | null;
  sector?: string | null;
  salary_range?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  salary_currency?: string | null;
  published_at?: string | null;
  valid_through?: string | null;
  featured?: boolean;
  image_url?: string | null;
  company_name?: string | null;
  company_logo_url?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  hiring_organization_name?: string | null;
  hiring_organization_logo?: string | null;
  hiring_organization_url?: string | null;
  job_location_street_address?: string | null;
  job_location_postal_code?: string | null;
  job_location_country?: string | null;
  parcours_id?: string | null;
}

// ─── Public queries ──────────────────────────────────────────────────────────

export async function getPublishedJobOffers(
  filters: JobOfferFilters = {}
): Promise<JobOffer[]> {
  let query = supabase
    .from('job_offers')
    .select('*')
    .eq('status', 'published')
    .order('featured', { ascending: false })
    .order('published_at', { ascending: false });

  if (filters.contractType && filters.contractType !== 'all') {
    query = query.eq('contract_type', filters.contractType);
  }

  if (filters.remoteType && filters.remoteType !== 'all') {
    query = query.eq('remote_type', filters.remoteType);
  }

  if (filters.locationCity && filters.locationCity.trim()) {
    // Escape ILIKE wildcards to prevent full-table scans via "%" injection.
    query = query.ilike('location_city', `%${escapeLike(filters.locationCity.trim())}%`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Erreur récupération offres publiées:', error);
    throw new Error('Impossible de récupérer les offres.');
  }

  return (data ?? []) as JobOffer[];
}

export async function getPublishedJobOfferBySlug(
  slug: string
): Promise<JobOffer | null> {
  const { data, error } = await supabase
    .from('job_offers')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();

  if (error) {
    console.error('Erreur récupération offre:', error);
    throw new Error('Impossible de récupérer cette offre.');
  }

  return data as JobOffer | null;
}

// ─── Admin queries ────────────────────────────────────────────────────────────

export async function getAllJobOffers(): Promise<JobOffer[]> {
  const { data, error } = await supabase
    .from('job_offers')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Erreur récupération toutes les offres:', error);
    throw new Error('Impossible de récupérer les offres.');
  }

  return (data ?? []) as JobOffer[];
}

export async function getJobOfferById(id: string): Promise<JobOffer | null> {
  const { data, error } = await supabase
    .from('job_offers')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Erreur récupération offre par id:', error);
    throw new Error('Impossible de récupérer cette offre.');
  }

  return data as JobOffer | null;
}

export async function createJobOffer(input: JobOfferInput): Promise<JobOffer> {
  const now = new Date().toISOString();
  const payload = {
    ...input,
    published_at: input.status === 'published' ? (input.published_at ?? now) : input.published_at ?? null,
    updated_at: now,
  };

  const { data, error } = await supabase
    .from('job_offers')
    .insert(payload)
    .select()
    .single();

  if (error) {
    console.error('Erreur création offre:', error);
    throw new Error('Impossible de créer l\'offre.');
  }

  return data as JobOffer;
}

export async function updateJobOffer(
  id: string,
  input: Partial<JobOfferInput>
): Promise<JobOffer> {
  const now = new Date().toISOString();
  const payload: Record<string, unknown> = { ...input, updated_at: now };

  if (input.status === 'published' && !input.published_at) {
    const existing = await getJobOfferById(id);
    if (!existing?.published_at) {
      payload.published_at = now;
    }
  }

  const { data, error } = await supabase
    .from('job_offers')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Erreur mise à jour offre:', error);
    throw new Error('Impossible de mettre à jour l\'offre.');
  }

  return data as JobOffer;
}

export async function archiveJobOffer(id: string): Promise<void> {
  const { error } = await supabase
    .from('job_offers')
    .update({ status: 'archived', updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('Erreur archivage offre:', error);
    throw new Error('Impossible d\'archiver l\'offre.');
  }
}

export async function deleteJobOffer(id: string): Promise<void> {
  const { error } = await supabase
    .from('job_offers')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Erreur suppression offre:', error);
    throw new Error('Impossible de supprimer l\'offre.');
  }
}

export async function duplicateJobOffer(id: string): Promise<JobOffer> {
  const original = await getJobOfferById(id);
  if (!original) throw new Error('Offre introuvable.');

  const { id: _id, created_at: _ca, updated_at: _ua, published_at: _pa, ...rest } = original;

  const input: JobOfferInput = {
    ...rest,
    title: `${rest.title} (copie)`,
    slug: `${rest.slug}-copie-${Date.now()}`,
    status: 'draft',
    published_at: null,
    featured: false,
  };

  return createJobOffer(input);
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-|-$/g, '');
}
